import { Afspraak, ReasonForm, PersonalDetailsForm, CookieAnswer, Klant, NederlandsAdres, OtherSubjectsForm } from '@ha/domain';
import { AdresBuilder, AfspraakBuilder, KlantBuilder } from '@ha/builders';
import { appointmentPattern } from '@ha/hypotheken-afspraak.constants';
import { v4 as uuid } from 'uuid';

import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpResponse } from '@angular/common/http';
import { FormGroup } from '@angular/forms';

import { ContactDetailsForm } from '@ap/personal-details';
import { ApDateService } from '@ap/date';
import { AddressForm } from '@ap/dutch-address';

import { AppointmentResource } from '../appointment.resource';

@Injectable({ providedIn: 'root' })
export class MortgageAppointmentService {
    private readonly appointmentResource = inject(AppointmentResource);
    private readonly orderId: string = uuid();

    getUUID(): string {
        return this.orderId;
    }

    createRequest(
        reasonFormGroup: FormGroup<ReasonForm>,
        personalDetailsFormGroup: FormGroup<PersonalDetailsForm>,
        addressFormGroup: FormGroup<AddressForm>,
        contactDetailsFormGroup: FormGroup<ContactDetailsForm>,
        otherSubjectsFormGroup: FormGroup<OtherSubjectsForm>
    ): Observable<HttpResponse<any>> {
        const reasonValues = reasonFormGroup.value;

        const afspraak: Afspraak = new AfspraakBuilder()
            .withKlant(this.getKlant(personalDetailsFormGroup, addressFormGroup, contactDetailsFormGroup, otherSubjectsFormGroup))
            .withReason(reasonValues.reason)
            .withHouseReason(reasonValues.houseReason)
            .withRefinance(reasonValues.refinance)
            .withExtraInfo(otherSubjectsFormGroup.value.otherSubjects)
            .withGoogleClickId(this.getGoogleClickId())
            .build();
        return this.appointmentResource.sendRequest(afspraak);
    }

    getKlant(
        personalDetailsFormGroup: FormGroup<PersonalDetailsForm>,
        addressFormGroup: FormGroup<AddressForm>,
        contactDetailsFormGroup: FormGroup<ContactDetailsForm>,
        otherSubjectsFormGroup: FormGroup<OtherSubjectsForm>
    ): Klant {
        const personalDetailsValues = personalDetailsFormGroup.value;
        const contactDetailsValues = contactDetailsFormGroup.value;
        const otherSubjectsValues = otherSubjectsFormGroup.value;

        return new KlantBuilder()
            .withBank(otherSubjectsValues.existingCustomer ? otherSubjectsValues.bank : null)
            .withGeslacht(personalDetailsValues.gender)
            .withVoornamen(personalDetailsValues.firstName)
            .withVoorletters(personalDetailsValues.initials)
            .withFamilienaam(personalDetailsValues.familyName)
            .withTussenvoegsel(personalDetailsValues.prefix ? personalDetailsValues.prefix : null)
            .withGeboorteDatum(ApDateService.format(personalDetailsValues.dateOfBirth, appointmentPattern.dateOfBirth))
            .withEmailadres(contactDetailsValues.emailAddress)
            .withTelefoonnummer(contactDetailsValues.phoneNumber)
            .withAdres(this.getAdres(addressFormGroup))
            .build();
    }

    private getAdres(addressFormGroup: FormGroup<AddressForm>): NederlandsAdres {
        const addressValues = addressFormGroup.value;

        return new AdresBuilder()
            .withhuisnummer(addressValues.houseNumber)
            .withHuisnummerToevoeging(addressValues.houseNumberSuffix ? addressValues.houseNumberSuffix : null)
            .withPostcode(addressValues.postalCode)
            .withPlaats(addressValues.city)
            .withStraat(addressValues.street)
            .build();
    }

    getGoogleClickId(): string {
        const cookie = this.getCookie('deq_pers_digitalData_l');
        if (cookie) {
            const jsonCookie = JSON.parse(cookie);
            const cookieData = jsonCookie as CookieAnswer;

            return cookieData?.['.*']?.data?.googleClickId;
        }
    }

    getCookie(name: string): string {
        const nameLenPlus = name.length + 1;
        return (
            document.cookie
                .split(';')
                .map((c) => c.trim())
                .filter((cookie) => cookie.substring(0, nameLenPlus) === `${name}=`)
                .map((cookie) => decodeURIComponent(cookie.substring(nameLenPlus)))[0] || null
        );
    }
}
